/**
 * Widget-Editor — Beratung je Entscheidung (Reisegruppen-Widget T5, Mockup entwurf-6.html,
 * Reiter „MAESTRO-Einstellungen“).
 *
 * Reine Regeln, kein React. Jede Entscheidung nennt
 *   - was empfohlen ist (`empfohlen`),
 *   - die Begründung in einem Satz (`begruendung`, die ehrliche, freigegebene Fassung aus dem Mockup),
 *   - und, wenn der Betreiber abweicht, einen Hinweis, was das voraussichtlich kostet (`hinweis`).
 *
 * Eine Abweichung ist NIE eine Sperre. Die einzige Ausnahme ist eine rechtliche Grenze: ein
 * Kachel-Ablauf ohne Link zu den Datenschutzhinweisen darf nicht live gehen (`sperre`). Sie
 * verhindert das Veröffentlichen, nie das Speichern.
 *
 * Ehrlichkeit vor Werbung: Die Begründungen behaupten keinen gemessenen Effekt, wo keiner belegt
 * ist. Was MAESTRO selbst misst, steht in der Karte „Wirkung“ (WirkungKarte.tsx).
 */
import type { AblaufForm, BedingungenForm } from "./ablauf-modell";

/** Was die Beratung vom Formular wissen muss (`EditorForm` erfüllt es strukturell). */
export interface BeratungsZustand {
  ablaufAn: boolean;
  ablauf: AblaufForm;
  privacyUrl: string;
  standardfelder: readonly string[];
  freieFelder: readonly { key: string; pflicht: boolean }[];
  bedingungen: BedingungenForm;
}

/** Was eine Entscheidung im Formular ändern darf (nur, wo die Wahl eine einzige klare Einstellung ist). */
export interface BeratungsAenderung {
  ablaufAn?: boolean;
}

export interface Entscheidung {
  key: string;
  gruppe: string;
  titel: string;
  optionen: string[];
  gewaehlt: number;
  empfohlen: number;
  begruendung: string;
  /** Wahl weicht von der Empfehlung ab. */
  abweichung: boolean;
  /** Was die Abweichung voraussichtlich kostet; `null` bei Empfehlung. */
  hinweis: string | null;
  /** Rechtliche Grenze: verhindert das Veröffentlichen (nie das Speichern); sonst `null`. */
  sperre: string | null;
  /** Wo man es ändert, wenn die Wahl nicht hier getroffen werden kann („Schritt Felder“); `null` = hier änderbar. */
  aendernIn: string | null;
}

interface Regel {
  key: string;
  gruppe: string;
  titel: string;
  optionen: string[];
  empfohlen: number;
  begruendung: string;
  /** Nur bewerten, wenn das zutrifft (z. B. „Kleinste Gruppe“ gibt es nur beim Kachel-Ablauf). */
  gilt?: (z: BeratungsZustand) => boolean;
  gewaehlt: (z: BeratungsZustand) => number;
  hinweis: string | Record<number, string>;
  sperre?: (z: BeratungsZustand, gewaehlt: number) => string | null;
  aendernIn?: string;
}

const MINDESTGRUPPE = 10;

function ersteZahl(text: string): number | null {
  const m = /\d+/.exec(text);
  return m ? Number(m[0]) : null;
}

const REGELN: Regel[] = [
  {
    key: "aufbau",
    gruppe: "Formular",
    titel: "Aufbau",
    optionen: ["Ein Tipp je Bildschirm, automatisch weiter", "Klassisches Formular (eine Seite oder zwei Schritte)"],
    empfohlen: 0,
    begruendung:
      "Erst die Gruppengröße, dann der Monat, jeweils ein Tipp und sofort weiter. Vorher steht über den Kacheln kurz „Ein Tipp genügt.“ (Screenreader: „Ein Tipp genügt, es geht direkt weiter.“), damit der Wechsel nicht überrascht (WCAG 3.2.2). Erst der letzte Bildschirm braucht die Tastatur. Der Anfang soll schnell wirken; ob weniger Leute abbrechen, misst MAESTRO je Bildschirm.",
    gewaehlt: (z) => (z.ablaufAn ? 0 : 1),
    hinweis: "Erlaubt und als Vergleich sinnvoll. Auf einer Seite ist die Tastatur allerdings sofort da, und der Anfang wirkt schwer.",
  },
  {
    key: "pflicht",
    gruppe: "Formular",
    titel: "Pflichtangaben",
    optionen: ["Höchstens 4: Gäste, Zeitraum, Name, E-Mail", "Mehr Pflichtfelder"],
    empfohlen: 0,
    begruendung: "Test je Feld: Könnte der Betrieb ohne diese Angabe nicht antworten? Nur diese vier bestehen den Test.",
    gewaehlt: (z) => (z.ablaufAn && !z.freieFelder.some((f) => f.pflicht) ? 0 : 1),
    hinweis:
      "Mehr Pflichtfelder bedeuten mehr Aufwand vor dem Absenden. Wer nur einen Preis will, bricht womöglich eher ab. MAESTRO zeigt Ihnen die Abbrüche je Schritt.",
    aendernIn: "Schritt „Felder“ (Pflicht-Haken) und „Ablauf“ (Kachel-Ablauf)",
  },
  {
    key: "freiw",
    gruppe: "Formular",
    titel: "Optionale Felder vor dem Absenden",
    optionen: ["Nur Tag und Menü-Wunsch, dezent – der Rest auf der Dankeseite", "Telefon, Firma, Nachricht im Formular"],
    empfohlen: 0,
    begruendung: "Auch optionale Felder wirken wie Arbeit. Auf der Dankeseite ist die Anfrage schon da, dort kosten sie nichts mehr.",
    gewaehlt: (z) => (z.standardfelder.some((k) => k !== "preferredMenu") || z.freieFelder.length > 0 ? 1 : 0),
    hinweis:
      "Jedes sichtbare Feld wirkt wie Arbeit, auch wenn es optional ist. Belastbare Zahlen dazu gibt es für Gastro-Anfragen nicht; MAESTRO misst den Unterschied in Ihrem Formular.",
    aendernIn: "Schritt „Felder“",
  },
  {
    key: "datum",
    gruppe: "Formular",
    titel: "Datum",
    optionen: ["Kalender: ein Wunschtag, auf Wunsch ein Ausweichtermin", "Einfaches Datumsfeld"],
    empfohlen: 0,
    begruendung:
      "Ein konkreter Tag macht die erste Antwort genauer, und der optionale Ausweichtermin fängt Gruppen auf, die noch flexibel sind. Ein Tipp im Kalender ist kürzer als ein Datum zu tippen.",
    gewaehlt: (z) => (z.ablaufAn ? 0 : 1),
    hinweis:
      "Der Kalender öffnet im passenden Monat, sperrt vergangene Tage und Schließtage (Einstellungen › Betrieb) und lässt sich mit der Tastatur bedienen.",
  },
  {
    key: "gaeste",
    gruppe: "Formular",
    titel: "Gästezahl",
    optionen: ["Bereiche antippen, genaue Zahl optional", "Zahl eintippen"],
    empfohlen: 0,
    begruendung:
      "Ein Tipp statt Tastatur. „Ungefähr reicht“ nimmt Druck, die genaue Zahl kommt später. Die Bereiche folgen den Grenzen für Freiplätze: ab 25 Reiseleitung frei, ab 40 Busfahrer.",
    gewaehlt: (z) => (z.ablaufAn ? 0 : 1),
    hinweis: "Eintippen öffnet die Tastatur schon in Schritt 1 und macht ihn schwerer.",
  },
  {
    key: "mindest",
    gruppe: "Formular",
    titel: "Kleinste Gruppe",
    optionen: [`${MINDESTGRUPPE} Personen, wie im Seitentext`, "Andere Zahl als im Seitentext"],
    empfohlen: 0,
    begruendung: "Formular und Seitentext müssen dieselbe Zahl nennen. Heute nennt das Formular 10, der Text 20.",
    gilt: (z) => z.ablaufAn,
    gewaehlt: (z) => (ersteZahl(z.ablauf.gaesteText) === MINDESTGRUPPE ? 0 : 1),
    hinweis: "Widersprüchliche Zahlen verunsichern. Bitte Seitentext und Formular angleichen.",
    aendernIn: "Schritt „Ablauf“ (Gruppengrößen)",
  },
  {
    key: "daten",
    gruppe: "Vertrauen",
    titel: "Datenschutz",
    optionen: ["Hinweis mit Link unter dem Knopf", "Ohne Link, nur der Hinweistext"],
    empfohlen: 0,
    begruendung:
      "Eine Anfrage vor Vertragsschluss braucht keine Einwilligung. Ein Hinweis mit Link genügt, der Knopf bleibt aktiv.",
    gewaehlt: (z) => (z.privacyUrl.trim() ? 0 : 1),
    hinweis:
      "Ohne Link steht nur, dass die Angaben zur Bearbeitung der Anfrage verarbeitet werden – rechtlich ist das eine Grauzone.",
    // Rechtliche Grenze: Wer personenbezogene Daten erhebt, muss informieren (Art. 13 DSGVO). Nur für den
    // neuen Kachel-Ablauf; bestehende klassische Formulare behalten ihr Verhalten (nur der Hinweis).
    sperre: (z, gewaehlt) =>
      z.ablaufAn && gewaehlt === 1
        ? "Ohne Link zu Ihren Datenschutzhinweisen kann der Kachel-Ablauf nicht live gehen. Wer Namen und E-Mail-Adressen erhebt, muss darüber informieren (Art. 13 DSGVO). Speichern geht weiter, nur das Publizieren ist gesperrt."
        : null,
    aendernIn: "Schritt „Datenschutz“",
  },
  {
    key: "infos",
    gruppe: "Vertrauen",
    titel: "Das Wichtigste auf einen Blick",
    optionen: ["Zeilen über dem Formular: Preis, Freiplatz, Bus", "Keine Zeilen"],
    empfohlen: 0,
    begruendung:
      "Informieren statt fragen: Genau danach vergleichen Veranstalter. Ein Preis steht nur da, wenn das Menü mit einem Katalog-Paket verknüpft ist; unbelegte Angaben zeigt MAESTRO nicht.",
    gilt: (z) => z.ablaufAn,
    gewaehlt: (z) => (z.ablauf.blick.some((b) => b.trim()) ? 0 : 1),
    hinweis: "Ohne diese Zeilen sucht der Besucher die Antworten auf der Seite oder fragt woanders an.",
    aendernIn: "Schritt „Ablauf“ (Auf einen Blick)",
  },
  {
    key: "folgefragen",
    gruppe: "Folgefragen",
    titel: "Folgefragen (bedingte Felder)",
    optionen: ["Optional (empfohlen)", "Pflicht"],
    empfohlen: 0,
    begruendung: "Folgefrage statt Pflichtfeld für alle – wer nicht betroffen ist, sieht die Frage gar nicht.",
    gilt: (z) => Object.keys(z.bedingungen).length > 0,
    gewaehlt: (z) => (Object.keys(z.bedingungen).some((ziel) => z.freieFelder.some((f) => f.key === ziel && f.pflicht)) ? 1 : 0),
    hinweis:
      "Achtung: Eine Pflicht-Folgefrage hält genau die großen Gruppen auf, die am meisten wert sind. Empfohlen: optional.",
    aendernIn: "Schritt „Felder“ (Pflicht-Haken am eigenen Feld)",
  },
];

/** Alle Entscheidungen, die zu diesem Formular gehören, mit Wahl, Abweichung, Hinweis und Sperre. */
export function beraten(z: BeratungsZustand): Entscheidung[] {
  const aus: Entscheidung[] = [];
  for (const r of REGELN) {
    if (r.gilt && !r.gilt(z)) continue;
    const gewaehlt = r.gewaehlt(z);
    const abweichung = gewaehlt !== r.empfohlen;
    const hinweis = !abweichung ? null : typeof r.hinweis === "string" ? r.hinweis : (r.hinweis[gewaehlt] ?? null);
    aus.push({
      key: r.key,
      gruppe: r.gruppe,
      titel: r.titel,
      optionen: r.optionen,
      gewaehlt,
      empfohlen: r.empfohlen,
      begruendung: r.begruendung,
      abweichung,
      hinweis,
      sperre: abweichung && r.sperre ? r.sperre(z, gewaehlt) : null,
      aendernIn: r.aendernIn ?? null,
    });
  }
  return aus;
}

/** „3 von 5“ + erklärender Satz — genau der Wortlaut des Mockups. */
export function auswertung(entscheidungen: readonly Entscheidung[]): { ok: number; gesamt: number; text: string } {
  const gesamt = entscheidungen.length;
  const abweichungen = entscheidungen.filter((e) => e.abweichung).length;
  const ok = gesamt - abweichungen;
  const text =
    abweichungen === 0
      ? "Alle Empfehlungen umgesetzt. Jede Abweichung ist erlaubt, MAESTRO sagt dann, was sie voraussichtlich kostet."
      : `${abweichungen} ${abweichungen > 1 ? "Abweichungen" : "Abweichung"} von der Empfehlung. Speichern geht trotzdem, die Hinweise sagen, was es voraussichtlich kostet.`;
  return { ok, gesamt, text };
}

/** Rechtliche Grenze, die das Veröffentlichen verhindert (nie das Speichern); `null` = frei. */
export function sperreZumPublizieren(z: BeratungsZustand): string | null {
  for (const e of beraten(z)) if (e.sperre) return e.sperre;
  return null;
}

/** Ändert das Formular, wenn die Entscheidung eine einzige klare Einstellung ist; sonst `null`. */
export function aenderungFuer(key: string, index: number): BeratungsAenderung | null {
  if (key === "aufbau" || key === "datum" || key === "gaeste") return { ablaufAn: index === 0 };
  return null;
}

/** Einbau-Tipps (Mockup „Einbau auf Ihrer Website“): keine Formular-Einstellung, sondern Rat für die Seite. */
export interface EinbauTipp {
  key: string;
  titel: string;
  grund: string;
}

export const EINBAU_TIPPS: EinbauTipp[] = [
  {
    key: "position",
    titel: "Im ersten Drittel, direkt nach dem Einstieg.",
    grund: "Schritt 1 soll auf dem Handy ohne Scrollen ganz zu sehen sein. Je weiter unten das Formular steht, desto weniger Besucher erreichen es.",
  },
  {
    key: "knoepfe",
    titel: "„Gruppe anfragen“ springt zum Formular, „Menüs ansehen“ zu den Menüs.",
    grund: "Heute springen beide zum Formular.",
  },
  {
    key: "menue",
    titel: "Menükarten mit „Anfragen“.",
    grund: "Der Menü-Wunsch geht still ans Formular mit (data-maestro-menue), Preise und Namen kommen nie von der Seite.",
  },
  {
    key: "handy",
    titel: "Auf dem Handy ein fester Knopf „Gruppe anfragen“, sobald das Formular aus dem Bild ist.",
    grund: "Nach dem Scrollen ist das Formular aus dem Bild. Ein fester Knopf bringt den Besucher mit einem Tipp zurück.",
  },
  {
    key: "zweitebox",
    titel: "Keine zweite Anfrage-Box direkt darunter.",
    grund: "Zwei ähnliche Aufforderungen verunsichern.",
  },
  {
    key: "sprache",
    titel: "Je Sprache die eigene Seite mit dem eigenen Widget.",
    grund: "Jede Sprache ist ein eigenes Widget mit eigenen Texten und eigener Kennung.",
  },
];
